import { Wedding } from '../../types';

export type WeddingCategory = 'active' | 'draft' | 'past';

export interface GroupedWeddings {
  active: Wedding[];
  draft: Wedding[];
  past: Wedding[];
}

/**
 * Determines the category of a wedding based on its date and status.
 * 
 * Categorization rules:
 * - draft: date is null/undefined/missing OR status is "draft" (if exists)
 * - past: date exists AND date < now OR status is "completed"/"archived" (if exists)
 * - active: everything else
 * 
 * @param wedding - The wedding object
 * @param now - The current date for comparison (defaults to new Date())
 * @returns The category of the wedding
 */
export function getWeddingCategory(
  wedding: Wedding,
  now: Date = new Date()
): WeddingCategory {
  // Check for explicit status field (if it exists in the future)
  // For now, we'll infer from date
  
  const date = wedding.date;
  
  // Draft: no date or empty date
  if (!date || date.trim() === '') {
    return 'draft';
  }
  
  // Try to parse the date
  const weddingDate = new Date(date);
  
  // If date is invalid, treat as draft
  if (isNaN(weddingDate.getTime())) {
    return 'draft';
  }
  
  // Compare dates (using local date, ignoring time)
  const weddingDateOnly = new Date(weddingDate.getFullYear(), weddingDate.getMonth(), weddingDate.getDate());
  const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // Past: wedding date is before today
  if (weddingDateOnly < nowDateOnly) {
    return 'past';
  }
  
  // Active: wedding date is today or in the future
  return 'active';
}

/**
 * Groups weddings into three categories: active, draft, and past.
 * 
 * @param weddings - Array of weddings to group
 * @param now - The current date for comparison (defaults to new Date())
 * @returns Object with three arrays: active, draft, and past
 */
export function groupWeddings(
  weddings: Wedding[],
  now: Date = new Date()
): GroupedWeddings {
  const grouped: GroupedWeddings = {
    active: [],
    draft: [],
    past: []
  };
  
  for (const wedding of weddings) {
    const category = getWeddingCategory(wedding, now);
    grouped[category].push(wedding);
  }
  
  return grouped;
}
